import { queryOptions } from '@tanstack/react-query';
import {
  promotionApi,
  type PromotionFilters,
  type PromotionListScope,
} from './promotion-api';

export const promotionKeys = {
  all: ['promotion'] as const,
  /**
   * The filters are PART of the key: they narrow the request server-side, so
   * two filter combinations are two different lists. Keying them out would
   * serve the previous filter's rows from cache and make the bar look inert.
   */
  list: (filters: PromotionFilters = {}, scope: PromotionListScope = { branches: [] }) =>
    [
      ...promotionKeys.all,
      'list',
      filters,
      scope.branches.map((branch) => branch.id),
    ] as const,
  productRows: (filters: PromotionFilters = {}) =>
    [...promotionKeys.all, 'product-rows', filters] as const,
  /** One promotion's authoritative terms, as read for the editor. */
  /** One promotion AT ONE BRANCH — the read is branch-scoped, so the key is. */
  detail: (id: string, restaurantId: string) =>
    [...promotionKeys.all, 'detail', id, restaurantId] as const,
};

export const promotionQueries = {
  list: (
    filters: PromotionFilters = {},
    scope: PromotionListScope = { branches: [] },
    options?: { enabled?: boolean },
  ) =>
    queryOptions({
      queryKey: promotionKeys.list(filters, scope),
      queryFn: ({ signal }) => promotionApi.list(filters, scope, { signal }),
      // Showcase (Витрина) mode reads each section from its own `*/published`
      // endpoint, so the page disables the sections that are not on screen and
      // only the active section's request fires.
      enabled:
        (options?.enabled ?? true) &&
        (filters.showcase ? true : scope.branches.length > 0),
    }),

  productRows: (filters: PromotionFilters = {}) =>
    queryOptions({
      queryKey: promotionKeys.productRows(filters),
      queryFn: ({ signal }) => promotionApi.productRows(filters, { signal }),
    }),

  /**
   * One promotion's own record, for seeding the edit modal. The table row it
   * would otherwise seed from is a JOINED `product × restaurant × promotion`
   * projection that names its product and branch by display NAME — so the form
   * had to match them back by string. This answers with `product_ids` and
   * `restaurant_id` directly, which is both exact and immune to two products
   * sharing a name.
   *
   * BRANCH-SCOPED, and the branch is part of the key: the endpoint now requires
   * `restaurant_id` and answers with the products the promotion covers AT THAT
   * BRANCH. Reading one branch's answer from another's cache would tick the
   * wrong checkboxes, so the two are cached apart.
   *
   * `staleTime: 0` so every open re-reads rather than replaying whatever the
   * table last cached.
   */
  detail: (id: string | undefined, restaurantId: string | undefined) =>
    queryOptions({
      queryKey: promotionKeys.detail(id ?? '', restaurantId ?? ''),
      // `enabled` guards the undefined cases, so both are present here.
      queryFn: ({ signal }) => promotionApi.get(id ?? '', restaurantId ?? '', { signal }),
      enabled: id !== undefined && restaurantId !== undefined && restaurantId !== '',
      staleTime: 0,
    }),
};
